import { NavLink, Outlet, useParams } from "react-router";
import {
  useVbsOrderShowQuery,
  useVbsOrderUpdateStateMutation,
} from "@/services/backend/vbs/orders/api/order-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Order } from "@/services/backend/vbs/orders/types/order";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DeleteOrderDialog } from "@/routes/processes/[processId]/vbs/orders/[orderId]/_components/delete-order-dialog";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import t from "@/lang/lang";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { VbsBreadcrumbs } from "@/routes/processes/[processId]/vbs/_components/vbs-breadcrumbs";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";

export function OrderRoute() {
  const { orderId } = useParams();

  const {
    data: order,
    isLoading,
    error,
  } = useVbsOrderShowQuery({
    id: orderId!,
  });

  const breadcrumbs = order && (
    <VbsBreadcrumbs customerId={order.customerId} processId={order.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/vbs/${order.processId!}/orders`}>
            {t("Auftragseingänge")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <ReferenceNumber referenceNumber={order.referenceNumber} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </VbsBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {order && <Page order={order} />}
    </ProcessPageMainTemplate>
  );
}

export function Page({ order }: { order: Order }) {
  return (
    <PersistenceContextProvider>
      <ErrValidationFailedContextProvider>
        <DocumentPage>
          <DocumentPageHeader>
            <TabNavigation />
            <DocumentPageActions>
              <PersistenceStateUsingContext className="mr-2" />
              <DeleteOrderDialog orderId={order.id} />
              <DocumentStateDropdown
                document={order}
                mutation={useVbsOrderUpdateStateMutation}
              />
            </DocumentPageActions>
          </DocumentPageHeader>
          <DocumentPageContent>
            <Outlet />
          </DocumentPageContent>
        </DocumentPage>
      </ErrValidationFailedContextProvider>
    </PersistenceContextProvider>
  );
}

function TabNavigation() {
  // The field names of each tab: the keys of order.ReadyForCustomer and of
  // the validation errors of the fields shown on the tab.
  const links = [
    {
      to: "data",
      label: t("Auftragseingang"),
      fieldName: ["referenceNumber", "date", "channel"],
    },
    { to: "treatment", label: t("Leistungen") },
    {
      to: "addresses-and-contacts",
      label: t("Adressen & Kontakte"),
      fieldName: [
        "serviceAddress",
        "postalAddress",
        "serviceContact",
        "representative",
      ],
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  return (
    <DocumentPageNavigation>
      {links.map((link) => (
        <DocumentPageNavigationLink
          key={link.to}
          to={link.to}
          fieldName={link.fieldName}
        >
          {link.label}
        </DocumentPageNavigationLink>
      ))}
    </DocumentPageNavigation>
  );
}
